// src/pages/HomePage.jsx — EBIOSENS Modern Cyber Risk Landing Page
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from 'framer-motion';
import {
  ArrowRight, ArrowDown, Plus, Menu, X, RotateCcw, ChevronDown, ChevronRight,
  Compass, Users, Waypoints, GitBranch, FileCheck2, ShieldCheck,
  Lock, Layers, FileText, Check, CheckCircle2,
  BarChart3, Eye, FileOutput, History, FileSpreadsheet,
  Sparkles, Database, Brain, Cpu, Server, FileDown, Zap,
  AlertTriangle, ArrowUpRight, Award, Network
} from 'lucide-react';
import EbiosensLogo from '../components/Brand/EbiosensLogo';

// 5 Ateliers EBIOS RM v1.5 with deep, realistic methodology & screenshot pairings
const ateliersData = [
  {
    n: '01',
    code: 'A1',
    title: 'Cadrage & Socle de Sécurité',
    subtitle: 'Périmètre, Valeurs Métier & Événements Redoutés',
    Icon: Compass,
    image: '/vids/valeurmetier.gif',
    fallbackImage: '/caps/VMs.png',
    badge: 'CADRAGE & SOCLE',
    desc: 'Définissez le périmètre de l’étude, identifiez les valeurs métier et leurs biens supports associés avec la qualification DICP (Disponibilité, Intégrité, Confidentialité, Preuve). Évaluez l’état d’application du socle de sécurité et les écarts de conformité.',
    inputs: [
      'Identification du contexte',
      'Cartographie des Valeurs Métier (VM) & Biens Supports',
      'Événements Redoutés (ER)',
      'Évaluation de la conformité du socle (ISO 27001, NIS 2, Guides EBIOS RM)'
    ],
    out: 'Fiche de cadrage d’analyse & Matrice des Valeurs Métier / Biens Supports',
    tags: ['PÉRIMÈTRE DICP', 'BIENS ESSENTIELS', 'ÉVÉNEMENTS REDOUTÉS', 'CONFORMITÉ SOCLE']
  },
  {
    n: '02',
    code: 'A2',
    title: 'Sources de Risque (SR & OV)',
    subtitle: 'Profils d’attaquants, motivations & objectifs visés',
    Icon: Users,
    image: '/vids/srov.gif',
    fallbackImage: '/caps/stakeholders.png',
    badge: 'MENACES & ACTEURS',
    desc: 'Cartographiez qui peut s’attaquer à votre organisation. Qualifiez les Sources de Risque (cybercriminels, attaquants étatiques, concurrents, initiés malveillants) et associez-les aux Objectifs Visés (OV) prioritaires selon leurs ressources et motivations.',
    inputs: [
      'Sélection typologique des Sources de Risque (SR)',
      'Évaluation du niveau de ressources et de la motivation',
      'Identification des Objectifs Visés (OV) sur vos biens essentiels',
      'Priorisation et retenue des couples SR / OV pertinents'
    ],
    out: 'Cartographie des profils d’attaquants & Couples SR/OV retenus',
    tags: ['CYBERCRIMINALITÉ', 'ÉTATIQUES', 'RESSOURCES', 'OBJECTIFS VISÉS']
  },
  {
    n: '03',
    code: 'A3',
    title: 'Scénarios Stratégiques',
    subtitle: 'Écosystème, parties prenantes & chemins d’attaque',
    Icon: Waypoints,
    image: '/vids/srov.gif',
    fallbackImage: '/caps/stakeholders.png',
    badge: 'ÉCOSYSTÈME & RADAR',
    desc: 'Évaluez la maturité cyber de votre écosystème (fournisseurs, prestataires, partenaires, filiales). Tracez les chemins d’attaque stratégiques reliant la source de risque à vos valeurs métier en exploitant les vulnérabilités de la chaîne d’approvisionnement.',
    inputs: [
      'Cartographie de l’écosystème & analyse de confiance des tiers',
      'Radar de cyber-maturité des partenaires et sous-traitants',
      'Modélisation des chemins d’attaque cyber et rebonds tiers',
      'Cotation de la gravité et vraisemblance stratégique'
    ],
    out: 'Radar de l’écosystème & Cartographie des Scénarios Stratégiques',
    tags: ['SUPPLY CHAIN', 'RADAR TIERS', 'CHEMINS D’ATTAQUE', 'RISQUE STRATÉGIQUE']
  },
  {
    n: '04',
    code: 'A4',
    title: 'Scénarios Opérationnels',
    subtitle: 'Modélisation technique appuyée sur MITRE ATT&CK',
    Icon: GitBranch,
    image: '/vids/op4.gif',
    fallbackImage: '/caps/operational_scenarios.png',
    badge: 'CTI & MITRE ATT&CK',
    desc: 'Traduisez les scénarios stratégiques en chaînes d’attaque techniques réelles. Exploitez la base CTI MITRE ATT&CK et ATLAS intégrée pour sélectionner tactiques, techniques et sous-techniques afin d’estimer la vraisemblance opérationnelle.',
    inputs: [
      'Connexion directe au référentiel MITRE ATT&CK (tactiques & techniques)',
      'Décomposition de la chaîne d’attaque (Kill Chain opérationnelle)',
      'Évaluation de la vraisemblance opérationnelle (de 1 à 4)',
      'Assistance IA pour la génération et la qualification des modes opératoires'
    ],
    out: 'Dossier des scénarios opérationnels & Matrices techniques d’attaque',
    tags: ['MITRE ATT&CK', 'CYBER KILL CHAIN', 'TECHNIQUES TTPs', 'VRAISEMBLANCE']
  },
  {
    n: '05',
    code: 'A5',
    title: 'Traitement du Risque & Fusion PTR',
    subtitle: 'Mesures de sécurité, risque résiduel & roadmap COMEX',
    Icon: FileCheck2,
    image: '/vids/ptr.gif',
    fallbackImage: '/caps/risques_residuels.png',
    badge: 'DÉCISION & REMÉDIATION',
    desc: 'Arbitrez les options de traitement (réduire, transférer, éviter, maintenir). Consolidez le Plan de Traitement des Risques (PTR) unifié, mesurez la réduction d’impact sur la matrice des risques résiduels et préparez les arbitrages budgétaires pour la direction.',
    inputs: [
      'Sélection des mesures de sécurité préventives, protectrices et réactives',
      'Recalcul instantané du risque résiduel (Gravité × Vraisemblance)',
      'Moteur exclusif de Fusion PTR pour mutualiser les mesures transversales',
      'Établissement du calendrier de déploiement et des responsabilités'
    ],
    out: 'Plan de Traitement des Risques signé',
    tags: ['PLAN DE TRAITEMENT', 'RISQUE RÉSIDUEL', 'FUSION PTR', 'VISA DIRECTION']
  }
];

// Key differentiators
const keyDifferentiators = [
  {
    icon: Database,
    logos: [
      { src: '/MITRE_ATTACK_logo_Lockup-black.svg', alt: 'MITRE ATT&CK' },
      { src: '/mitre_atlas_logo.jpg', alt: 'MITRE ATLAS' }
    ],
    title: 'Cyber Threat Intelligence & MITRE ATT&CK',
    badge: 'THREAT-INFORMED',
    desc: 'Ne travaillez plus sur des scénarios abstraits. EBIOSENS intègre nativement les référentiels MITRE ATT&CK et ATLAS pour adosser vos scénarios opérationnels aux tactiques, techniques et modes opératoires (TTPs) réels observés sur le terrain cyber.'
  },
  {
    icon: Layers,
    title: 'Moteur Exclusif de Fusion PTR™',
    badge: 'MUTUALISATION',
    desc: 'Une mesure de sécurité protège souvent plusieurs scénarios. Notre module de fusion consolide automatiquement vos plans d’action, supprime les doublons budgétaires et génère une feuille de route SSI unifiée et actionnable pour votre COMEX.'
  },
  {
    icon: Brain,
    title: 'Copilote IA & Rigueur Méthodologique',
    badge: 'ASSISTANCE MÉTIER',
    desc: 'Accélérez la formalisation de vos ateliers sans jamais transiger avec les référentiels et guides de la méthode.'
  },
  {
    icon: Users,
    title: 'Collaboration Multi-Analystes & Traçabilité',
    badge: 'TEMPS RÉEL',
    desc: 'Conduisez vos ateliers en équipe : indicateurs de présence en direct, notifications de modifications en temps réel, historique de versions avec snapshots restaurables et accès auditeur en lecture seule sécurisée.'
  }
];

// Interactive Risk Matrix Simulation Data
const scenarios = [
  { id: 'S-01', label: 'Exfiltration massive des bases RH & Données Clients', g: 3, v: 2, residualG: 2, residualV: 1, type: 'Cybercriminalité' },
  { id: 'S-02', label: 'Ransomware généralisé avec blocage de production', g: 4, v: 3, residualG: 3, residualV: 1, type: 'Attaque ciblée' },
  { id: 'S-03', label: 'Compromission via accès distant prestataire VPN', g: 2, v: 3, residualG: 1, residualV: 1, type: 'Supply Chain' },
  { id: 'S-04', label: 'Sabotage logique des systèmes industriels (OT)', g: 4, v: 2, residualG: 2, residualV: 1, type: 'Acteur étatique' },
  { id: 'S-05', label: 'Vol de propriété intellectuelle par rebond filiale', g: 3, v: 3, residualG: 2, residualV: 2, type: 'Espionnage' },
  { id: 'S-06', label: 'Usurpation d’identité compte Administrateur Cloud', g: 4, v: 4, residualG: 2, residualV: 1, type: 'Credential Stuffing' },
];

const LVL = [
  { name: 'NÉGLIGEABLE', bg: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30', cellBg: 'bg-emerald-950/60 hover:bg-emerald-900/80', dot: '#34d399' },
  { name: 'MODÉRÉ', bg: 'bg-amber-500/20 text-amber-300 border-amber-500/30', cellBg: 'bg-amber-950/60 hover:bg-amber-900/80', dot: '#fbbf24' },
  { name: 'IMPORTANT', bg: 'bg-orange-500/20 text-orange-300 border-orange-500/30', cellBg: 'bg-orange-950/60 hover:bg-orange-900/80', dot: '#fb923c' },
  { name: 'CRITIQUE', bg: 'bg-red-500/20 text-red-300 border-red-500/30', cellBg: 'bg-red-950/70 hover:bg-red-900/90', dot: '#f87171', hatch: true },
];

const getLevelIndex = (score) => (score <= 3 ? 0 : score <= 5 ? 1 : score === 6 ? 2 : 3);

// Animation helper
const R = ({ children, delay = 0, className = '' }) => (
  <motion.div
    className={className}
    style={{ willChange: 'transform, opacity' }}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

const SectionHeading = ({ number, badge, title, subtitle, centered = false }) => (
  <div className={`mb-12 ${centered ? 'text-center max-w-3xl mx-auto' : 'max-w-3xl'}`}>
    <R>
      <div className={`inline-flex items-center gap-2 text-[12px] font-bold tracking-[0.14em] text-blue-400 uppercase mb-3 ${centered ? 'justify-center' : ''}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
        {number && <span>{number} — </span>}
        <span>{badge}</span>
      </div>
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-[1.08]">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">
          {subtitle}
        </p>
      )}
    </R>
  </div>
);

// Interactive Matrix Component
function InteractiveMatrix() {
  const [hoveredScenario, setHoveredScenario] = useState(null);
  const [showResidual, setShowResidual] = useState(false);
  const [sigCount, setSigCount] = useState(0);

  const byCell = {};
  scenarios.forEach((s) => {
    const g = showResidual ? s.residualG : s.g;
    const v = showResidual ? s.residualV : s.v;
    const key = `${g}-${v}`;
    if (!byCell[key]) byCell[key] = [];
    byCell[key].push(s);
  });

  return (
    <div className="rounded-2xl border border-white/10 bg-[var(--bg-card)]/90 backdrop-blur-xl shadow-2xl overflow-hidden">
      {/* Matrix Header */}
      <div className="px-6 py-4 border-b border-white/10 flex flex-wrap items-center justify-between gap-4 bg-white/[0.02]">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse" />
          <span className="text-sm font-bold text-white tracking-wide">
            MATRICE D’ANALYSE 4×4 — {showResidual ? 'RISQUES RÉSIDUELS APRÈS MESURES' : 'RISQUES BRUTS'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400 mr-1">Vue :</span>
          <button
            onClick={() => setShowResidual(false)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${!showResidual ? 'bg-blue-600 text-white shadow-md' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}
          >
            Risques Bruts
          </button>
          <button
            onClick={() => setShowResidual(true)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${showResidual ? 'bg-emerald-600 text-white shadow-md' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}
          >
            Risques Résiduels (PTR)
          </button>
        </div>
      </div>

      <div className="p-6 lg:p-8 grid lg:grid-cols-12 gap-8 items-center">
        {/* Matrix Grid */}
        <div className="lg:col-span-7">
          <div className="flex gap-4">
            <div className="flex flex-col justify-center items-center">
              <span className="text-[11px] font-bold tracking-[0.2em] text-slate-400 [writing-mode:vertical-rl] rotate-180 py-4">
                VRAISEMBLANCE (1 À 4)
              </span>
            </div>

            <div className="flex-1">
              <div className="grid grid-rows-4 gap-2">
                {[4, 3, 2, 1].map((v) => (
                  <div key={v} className="grid grid-cols-[24px_1fr] gap-3 items-center">
                    <span className="text-xs font-bold text-slate-400 text-right">{v}</span>
                    <div className="grid grid-cols-4 gap-2">
                      {[1, 2, 3, 4].map((g) => {
                        const cellKey = `${g}-${v}`;
                        const cellScenarios = byCell[cellKey] || [];
                        const lvlIdx = getLevelIndex(g + v);
                        const lvl = LVL[lvlIdx];
                        const isHovered = hoveredScenario && (showResidual ? (hoveredScenario.residualG === g && hoveredScenario.residualV === v) : (hoveredScenario.g === g && hoveredScenario.v === v));

                        return (
                          <div
                            key={g}
                            className={`h-16 rounded-xl border border-white/10 flex flex-col justify-between p-2 transition-all duration-200 cursor-pointer relative ${lvl.cellBg} ${isHovered ? 'ring-2 ring-white scale-[1.03] shadow-lg z-10' : ''}`}
                            onMouseEnter={() => cellScenarios.length > 0 && setHoveredScenario(cellScenarios[0])}
                            onMouseLeave={() => setHoveredScenario(null)}
                          >
                            <span className="text-[10px] font-mono text-slate-400">G{g}V{v}</span>
                            <div className="flex flex-wrap gap-1">
                              {cellScenarios.map((sc) => (
                                <span
                                  key={sc.id}
                                  className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-black/60 text-white border border-white/20"
                                >
                                  {sc.id}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-[24px_1fr] gap-3 mt-3">
                <span />
                <div className="grid grid-cols-4 gap-2 text-center text-xs font-bold text-slate-400">
                  <span>1</span>
                  <span>2</span>
                  <span>3</span>
                  <span>4</span>
                </div>
              </div>

              <div className="text-center text-[11px] font-bold tracking-[0.2em] text-slate-400 mt-2">
                GRAVITÉ D'IMPACT (1 À 4)
              </div>
            </div>
          </div>
        </div>

        {/* Matrix Inspector & Scenarios List */}
        <div className="lg:col-span-5 flex flex-col justify-between h-full bg-white/[0.02] p-5 rounded-xl border border-white/5">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-blue-400 mb-3 flex items-center justify-between">
              <span>Scénarios Identifiés ({scenarios.length})</span>
              <span className="text-[11px] text-slate-400">Survolez un scénario</span>
            </div>

            <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
              {scenarios.map((s) => {
                const currentG = showResidual ? s.residualG : s.g;
                const currentV = showResidual ? s.residualV : s.v;
                const lvl = LVL[getLevelIndex(currentG + currentV)];
                const isActive = hoveredScenario?.id === s.id;

                return (
                  <div
                    key={s.id}
                    onMouseEnter={() => setHoveredScenario(s)}
                    onMouseLeave={() => setHoveredScenario(null)}
                    className={`p-2.5 rounded-lg border text-left transition-all cursor-pointer ${isActive ? 'border-blue-400 bg-blue-600/20' : 'border-white/5 bg-white/[0.02] hover:bg-white/[0.05]'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-bold text-white bg-white/10 px-1.5 py-0.5 rounded">
                          {s.id}
                        </span>
                        <span className="text-xs font-medium text-slate-200 truncate max-w-[200px]">
                          {s.label}
                        </span>
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${lvl.bg}`}>
                        G{currentG}×V{currentV}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Decision Visa Block */}
          <div className="mt-6 pt-4 border-t border-white/10 flex items-center">
            <div>
              <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                Visa d'Arbitrage COMEX / RSSI
              </div>
              <div
                onClick={() => setSigCount(sigCount + 1)}
                className="mt-1 cursor-pointer group flex items-center gap-2 text-xs font-semibold text-blue-400 hover:text-blue-300"
                title="Cliquer pour simuler la signature électronique"
              >
                <span>Approuvé & Signé électroniquement</span>
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// 5 Ateliers Interactive Tabbed Explorer
function WorkshopsStudio() {
  const [activeTabIdx, setActiveTabIdx] = useState(0);
  const activeAtelier = ateliersData[activeTabIdx];
  const Icon = activeAtelier.Icon;

  return (
    <div className="rounded-2xl border border-white/10 bg-[var(--bg-card)] overflow-hidden shadow-2xl">
      {/* Studio Workshop Tabs */}
      <div className="grid grid-cols-2 sm:grid-cols-5 border-b border-white/10 divide-x divide-white/10 bg-white/[0.02]">
        {ateliersData.map((a, idx) => {
          const isSelected = activeTabIdx === idx;
          const TabIcon = a.Icon;
          return (
            <button
              key={a.n}
              onClick={() => setActiveTabIdx(idx)}
              className={`p-4 md:p-5 text-left transition-all relative flex flex-col justify-between ${isSelected ? 'bg-[var(--bg-elevated)]' : 'hover:bg-white/[0.04]'}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-mono font-bold tracking-wider ${isSelected ? 'text-blue-400' : 'text-slate-400'}`}>
                  {a.code}
                </span>
                <TabIcon className={`w-4 h-4 ${isSelected ? 'text-blue-400' : 'text-slate-400'}`} />
              </div>
              <div className={`text-sm font-bold truncate ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                {a.title.split('&')[0]}
              </div>
              {isSelected && (
                <motion.div
                  layoutId="activeTabIndicator"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.8)]"
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Selected Atelier Content & Real Preview */}
      <div className="p-6 md:p-10 grid lg:grid-cols-12 gap-8 items-center">
        {/* Left: Methodology details */}
        <div className="lg:col-span-6 space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold tracking-wide">
            <Icon className="w-3.5 h-3.5" />
            ATELIER {activeAtelier.n} — {activeAtelier.badge}
          </div>

          <div>
            <h3 className="text-2xl md:text-3xl font-extrabold text-white">
              {activeAtelier.title}
            </h3>
            <p className="text-slate-300 text-sm md:text-base leading-relaxed mt-3">
              {activeAtelier.desc}
            </p>
          </div>

          <div className="space-y-3 pt-2">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400">
              Étapes méthodologiques & données produites :
            </div>
            <ul className="space-y-2">
              {activeAtelier.inputs.map((inp) => (
                <li key={inp} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-200">
                  <CheckCircle2 className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                  <span>{inp}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="pt-4 border-t border-white/10">
            <div className="text-[11px] font-bold uppercase tracking-wider text-blue-400 mb-1">
              Livrable EBIOS RM produit en fin d’atelier
            </div>
            <div className="text-sm font-semibold text-white flex items-center gap-2">
              <FileCheck2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>{activeAtelier.out}</span>
            </div>
          </div>
        </div>

        {/* Right: UI Capture with fallback handling */}
        <div className="lg:col-span-6">
          <div className="rounded-xl overflow-hidden border border-white/15 bg-slate-950/80 shadow-2xl relative group">
            {/* Window header bar */}
            <div className="h-9 bg-slate-900 border-b border-white/10 px-4 flex items-center justify-between">
              <div className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
              </div>
              <span className="text-[11px] font-mono text-slate-400 tracking-wider">
                EBIOSENS · {activeAtelier.code} · WORKFLOW RÉEL
              </span>
              <div className="w-8" />
            </div>

            {/* Screen Image with graceful fallback — 100% uncropped */}
            <div className="relative bg-slate-950 overflow-hidden">
              <img
                key={activeAtelier.code}
                src={activeAtelier.image}
                alt={activeAtelier.title}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = activeAtelier.fallbackImage;
                }}
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>

            {/* Chassis Footer Status */}
            <div className="px-4 py-2.5 bg-slate-900 border-t border-white/10 flex items-center justify-between text-[11px] text-slate-300 font-mono">
              <span className="truncate mr-2 font-medium">{activeAtelier.title}</span>
              <span className="text-blue-400 font-bold shrink-0">{activeAtelier.code} · CONFORME EBIOS RM</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Enterprise FAQ List
const enterpriseFAQ = [
  {
    q: 'EBIOSENS est-il strictement conforme à la méthode EBIOS RM v1.5 ?',
    a: 'Oui. EBIOSENS applique rigoureusement le guide officiel EBIOS RM v1.5 : terminologie officielle (Valeurs Métier, Biens Supports, Événements Redoutés, Couples SR/OV, Scénarios Stratégiques et Opérationnels) et livrables conformes aux exigences des risk managers.'
  },
  {
    q: 'Comment fonctionne le module exclusif de « Fusion des PTR » ?',
    a: 'Dans une analyse d’envergure, une même mesure de sécurité (ex. déploiement d’un EDR, durcissement Active Directory, MFA sur l’accès distant) adresse souvent plusieurs scénarios de risque. EBIOSENS regroupe et consolide automatiquement ces mesures transversales, éliminant les doublons budgétaires et générant un Plan de Traitement des Risques (PTR) unifié et prêt pour le COMEX.'
  },
  {
    q: 'Comment s’intègre le référentiel MITRE ATT&CK & ATLAS ?',
    a: 'Dans l’Atelier 4 (Scénarios Opérationnels), EBIOSENS se connecte aux référentiels MITRE ATT&CK et MITRE ATLAS. Vous sélectionnez les tactiques, techniques et sous-techniques réelles employées par les attaquants (TTPs), ce qui garantit que vos scénarios cyber reposent sur l’état de l’art de la menace (Threat-Informed Defense).'
  },
  {
    q: 'Comment plusieurs analystes peuvent-ils travailler ensemble sans s’écraser ?',
    a: 'La plateforme intègre une collaboration temps réel sécurisée : visualisation des analystes connectés, notifications instantanées des modifications, historique des versions avec snapshots restaurables et traçabilité horodatée de chaque action.'
  },
  {
    q: 'Quels sont les livrables et formats exportables ?',
    a: 'En un clic, générez un Dossier d’Analyse complet au format Word (.docx) structuré selon le modèle officiel EBIOS RM, des rapports exécutifs de synthèse au format HTML dynamique avec graphiques, ainsi que des exports CSV exhaustifs pour intégration dans vos outils GRC ou de gouvernance.'
  },
  {
    q: 'Où sont hébergées les données et la solution peut-elle être déployée On-Premise ?',
    a: 'EBIOSENS est édité et maintenu en France par ACG Cybersecurity. La solution se déploie selon vos impératifs de souveraineté : sur vos propres serveurs (On-Premise), en environnement déconnecté (Air-Gap pour les OIV / OSE), ou sur cloud privé souverain en France / Union Européenne.'
  }
];

// Nav items definition
const NAV_ITEMS = [
  { id: 'ateliers',        label: '5 ATELIERS' },
  { id: 'differentiateurs', label: 'CTI & PTR' },
  { id: 'matrice',         label: 'MATRICE' },
  { id: 'livrables',       label: 'LIVRABLES' },
  { id: 'securite',        label: 'CONFORMITÉ' },
];

// Smooth scroll handler — uses native scrollIntoView (CSS scroll-behavior handles easing)

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [parisTime, setParisTime] = useState('');
  const [activeSection, setActiveSection] = useState(null);

  // Smooth scroll handler
  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    const el = document.getElementById(sectionId);
    if (!el) return;
    if (mobileMenuOpen) setMobileMenuOpen(false);
    setActiveSection(sectionId);
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Update hash without triggering router navigation
    window.history.replaceState(null, '', `#${sectionId}`);
  };

  // IntersectionObserver to track active section while scrolling
  useEffect(() => {
    const observers = [];
    NAV_ITEMS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id);
        },
        { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  useEffect(() => {
    const updateTime = () => {
      setParisTime(
        new Date().toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZone: 'Europe/Paris'
        })
      );
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[var(--bg-canvas)] text-slate-100 antialiased selection:bg-blue-600 selection:text-white overflow-x-hidden">
      {/* Design System Tokens */}
      <style>{`
        :root {
          --bg-canvas: #070B18;
          --bg-surface: #0D162D;
          --bg-card: #131E3D;
          --bg-elevated: #1B2952;
          --bd: rgba(255, 255, 255, 0.08);
          --bd-strong: rgba(255, 255, 255, 0.16);
          --accent-blue: #3B82F6;
          --accent-indigo: #6366F1;
        }
        html { scroll-behavior: smooth; }
        section[id] { scroll-margin-top: 80px; }
      `}</style>

      {/* Background Ambience & Grid */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute -top-[20%] left-1/2 -translate-x-1/2 w-[85vw] h-[55vw] bg-gradient-to-b from-blue-600/12 via-indigo-600/8 to-transparent rounded-full blur-[140px]" />
        <div className="absolute top-[40%] -left-[10%] w-[45vw] h-[45vw] bg-sky-500/6 rounded-full blur-[120px]" />
        <div className="absolute bottom-[10%] -right-[10%] w-[50vw] h-[50vw] bg-indigo-500/6 rounded-full blur-[130px]" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)`,
            backgroundSize: '24px 24px'
          }}
        />
      </div>

      {/* Sovereign Top Micro-bar */}
      <div className="relative z-20 border-b border-[var(--bd)] bg-white/[0.02] backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between text-[11px] font-medium tracking-wider text-slate-400">
          <div className="flex items-center gap-2 truncate">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-400" />
            <span className="font-semibold text-slate-300">ACG CYBERSECURITY</span>
            <span className="hidden sm:inline text-white/20">|</span>
            <span className="hidden sm:inline">ANALYSE & PILOTAGE DES RISQUES SELON LA MÉTHODE EBIOS RM v1.5</span>
          </div>
          <div className="flex items-center gap-3 shrink-0 text-slate-400">
            <span className="hidden md:inline font-mono text-slate-300">SOUVERAINETÉ FRANCE & UE</span>
            <span className="text-white/20">·</span>
            <span className="font-mono text-slate-300 tabular-nums">{parisTime} PARIS</span>
          </div>
        </div>
      </div>

      {/* Main Sticky Navbar */}
      <header className="sticky top-0 z-40 bg-[var(--bg-canvas)]/90 backdrop-blur-xl border-b border-[var(--bd)] transition-all">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" style={{ height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          {/* Logo & Brand */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <EbiosensLogo size={30} variant="dark" withWordmark />
            <div className="hidden xl:block border-l border-[var(--bd-strong)] pl-2.5">
              <span className="text-[9px] tracking-[0.12em] uppercase font-bold text-blue-400 block leading-tight whitespace-nowrap">
                Plateforme EBIOS RM
              </span>
              <span className="text-[10px] font-semibold text-slate-400 whitespace-nowrap">
                Édition Entreprise
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2 whitespace-nowrap">
            {NAV_ITEMS.map(({ id, label }) => {
              const isActive = activeSection === id;
              return (
                <a
                  key={id}
                  href={`#${id}`}
                  onClick={(e) => handleNavClick(e, id)}
                  className="relative px-3 py-1.5 text-[11px] font-bold tracking-[0.08em] rounded-md transition-colors duration-200 group"
                  style={{ color: isActive ? '#93c5fd' : '#cbd5e1' }}
                >
                  {/* Animated background pill */}
                  {isActive && (
                    <motion.span
                      layoutId="navPill"
                      className="absolute inset-0 rounded-md bg-blue-500/12 border border-blue-500/25"
                      transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                    />
                  )}
                  <span className="relative z-10 group-hover:text-blue-300 transition-colors duration-150">
                    {label}
                  </span>
                  {/* Bottom accent line */}
                  {isActive && (
                    <motion.span
                      layoutId="navUnderline"
                      className="absolute bottom-0 left-3 right-3 h-px bg-blue-400 rounded-full shadow-[0_0_6px_rgba(96,165,250,0.8)]"
                      transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                    />
                  )}
                </a>
              );
            })}
          </nav>

          {/* Header Action Buttons */}
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            <Link
              to="/login"
              className="text-[11px] font-bold text-slate-300 hover:text-white px-3 py-2 rounded-lg transition-colors whitespace-nowrap"
            >
              SE CONNECTER
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-[11px] px-4 py-2 rounded-lg shadow-lg shadow-blue-900/30 transition-all hover:scale-[1.02] whitespace-nowrap"
            >
              <span>OUVRIR L'ESPACE</span>
              <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {/* Mobile Menu Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-xl border border-[var(--bd)] text-slate-300 hover:text-white hover:bg-white/5 transition-colors shrink-0"
            aria-label="Menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-[var(--bd)] bg-[var(--bg-canvas)] px-6 py-6 space-y-4">
            <nav className="space-y-1">
              {[
                ['01', 'Les 5 Ateliers EBIOS RM', 'ateliers'],
                ['02', 'CTI & Fusion PTR™', 'differentiateurs'],
                ['03', 'Simulateur de Matrice', 'matrice'],
                ['04', 'Livrables & Rapports', 'livrables'],
                ['05', 'Conformité & Sécurité', 'securite']
              ].map(([num, label, id]) => (
                <a
                  key={num}
                  href={`#${id}`}
                  onClick={(e) => handleNavClick(e, id)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-200 font-semibold text-sm transition-colors ${activeSection === id ? 'bg-blue-500/10 text-blue-300 border border-blue-500/20' : 'hover:bg-white/5 hover:text-blue-400'}`}
                >
                  <span className="text-xs font-mono text-blue-400">{num}</span>
                  <span>{label}</span>
                </a>
              ))}
            </nav>
            <div className="pt-4 border-t border-[var(--bd)] flex flex-col gap-2">
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full text-center py-3 rounded-xl border border-[var(--bd-strong)] text-white font-bold text-xs"
              >
                SE CONNECTER
              </Link>
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full text-center py-3 rounded-xl bg-blue-600 text-white font-bold text-xs flex items-center justify-center gap-2"
              >
                <span>ACCÉDER À EBIOSENS</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* HERO SECTION — CENTERED HEADLINE + SUBHEADLINE / VISUAL ROW */}
      <section className="relative z-10 pt-10 pb-14 lg:pt-16 lg:pb-20 border-b border-[var(--bd)] overflow-hidden">
        <style>{`
          .hero-shimmer {
            background: linear-gradient(100deg, #60a5fa 20%, #c7d2fe 40%, #818cf8 50%, #60a5fa 70%);
            background-size: 200% auto;
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
            animation: hero-shimmer 6s linear infinite;
          }
          @keyframes hero-shimmer {
            to { background-position: 200% center; }
          }
          .hero-glow {
            animation: hero-glow-pulse 7s ease-in-out infinite;
          }
          @keyframes hero-glow-pulse {
            0%, 100% { opacity: 0.45; transform: scale(1); }
            50% { opacity: 0.75; transform: scale(1.06); }
          }
          @media (prefers-reduced-motion: reduce) {
            .hero-shimmer, .hero-glow { animation: none; }
          }
        `}</style>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Centered first headline sentence, full width above */}
          <div className="mx-auto max-w-3xl text-center">
            <R>
              {/* Primary Headline — first sentence, centered block */}
              <h1 className="font-black tracking-tight leading-[1.05] text-[32px] sm:text-[44px] lg:text-[50px] text-white text-center">
                Menez vos analyses EBIOS RM.
              </h1>
            </R>
          </div>

          {/* Second sentence beside dashboard visual (side-by-side on lg, stacked on mobile) */}
          <div className="mx-auto mt-8 grid max-w-6xl items-center gap-10 lg:grid-cols-2 lg:gap-12">
            {/* Second headline sentence + description */}
            <div className="text-center lg:text-left">
              <R delay={0.06}>
                <p className="hero-shimmer font-extrabold tracking-tight leading-[1.1] text-[28px] sm:text-[36px] lg:text-[40px]">
                  De l’atelier 1 au dossier signé.
                </p>
                <p className="mt-5 mx-auto lg:mx-0 max-w-xl text-[15px] sm:text-[16px] text-slate-300 leading-relaxed">
                  Cadrez vos biens essentiels, cartographiez les scénarios d'attaque avec MITRE ATT&CK et consolidez votre plan de traitement des risques. Une plateforme collaborative, assistée par l'intelligence artificielle, pour produire des livrables rigoureux, conformes aux exigences EBIOS RM.
                </p>
              </R>
            </div>

            {/* Dashboard visual chassis */}
            <div className="relative">
              {/* Soft animated glow behind chassis */}
              <div aria-hidden="true" className="hero-glow pointer-events-none absolute -inset-6 rounded-[28px] bg-gradient-to-br from-blue-600/25 via-indigo-500/15 to-transparent blur-3xl" />
              <R delay={0.16} className="relative">
                <div className="relative rounded-2xl border border-white/15 bg-slate-950/90 shadow-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-blue-500/30 hover:shadow-blue-900/40 hover:shadow-2xl">
                  {/* Chassis Window Header */}
                  <div className="h-10 bg-slate-900 border-b border-white/10 px-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
                      <span className="ml-3 font-mono text-[11px] text-slate-400 truncate max-w-[240px] sm:max-w-none">
                        EBIOSENS — TABLEAU DE BORD D'ANALYSE
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-emerald-400 flex items-center gap-1.5 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        SESSION ACTIVE
                      </span>
                    </div>
                  </div>

                  {/* Real Dashboard Animated GIF — 100% uncropped */}
                  <div className="relative bg-slate-950 overflow-hidden">
                    <img
                      src="/vids/dashboard.gif"
                      alt="Aperçu animé du tableau de bord EBIOSENS"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = '/caps/dashboard.png';
                      }}
                      className="w-full h-auto block"
                      loading="eager"
                    />
                  </div>

                  {/* Chassis Footer Bar */}
                  <div className="px-4 py-2.5 bg-slate-900 border-t border-white/10 flex items-center justify-between text-[11px] text-slate-300 font-mono">
                    <span className="text-slate-400">REF. EBS-2025-014</span>
                    <span className="text-blue-400 font-bold">5 ATELIERS SYNCHRONISÉS</span>
                  </div>
                </div>
              </R>
            </div>
          </div>

          {/* Centered supporting content below */}
          <div className="mx-auto mt-10 max-w-3xl text-center">
            <R delay={0.1}>
              {/* Action Buttons */}
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/login"
                  className="group inline-flex items-center gap-2.5 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs uppercase tracking-wider px-6 py-3.5 rounded-lg shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-900/50"
                >
                  <span>OUVRIR LA PLATEFORME</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>

                <a
                  href="#ateliers"
                  onClick={(e) => handleNavClick(e, 'ateliers')}
                  className="inline-flex items-center gap-2 border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] hover:border-white/25 text-slate-200 font-bold text-xs uppercase tracking-wider px-6 py-3.5 rounded-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/40"
                >
                  <span>VOIR LES 5 ATELIERS</span>
                  <ArrowDown className="w-4 h-4 text-slate-400" />
                </a>
              </div>

              {/* Architecture & Features Chips */}
              <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2.5 text-xs text-slate-300">
                <div className="flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                  <span>Workflow 5 ateliers</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                  <span>Base CTI MITRE ATT&CK</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                  <span>Assistance IA intégrée</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                  <span>Exports multi-formats</span>
                </div>
              </div>

              {/* Endorsement Logos — ANSSI/EBIOS RM logos hidden on homepage */}
              <div className="pt-4 flex items-center justify-center gap-3 text-xs text-slate-400">
                <span className="font-mono text-[10px] tracking-wider uppercase text-slate-400">Édité par</span>
                <img src="/acg-logo.png" alt="ACG Cybersecurity" className="h-7 w-auto rounded bg-white p-1 border border-white/10" />
                <span className="text-[11px] font-mono text-slate-400 ml-1">Référentiel EBIOS RM v1.5</span>
              </div>
            </R>
          </div>
        </div>
      </section>

      {/* PAIN POINTS: BEFORE VS AFTER (THE PARADIGM SHIFT) */}
      <section className="relative z-10 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            number="01"
            badge="TABLEUR VS PLATEFORME"
            title="Dites adieu aux tableurs Excel ingérables."
            subtitle="Pourquoi les RSSI et cabinets de conseil abandonnent les feuilles de calcul pour centraliser leurs analyses sur EBIOSENS."
            centered
          />

          <div className="relative grid md:grid-cols-2 gap-6 lg:gap-8 items-stretch mt-12">
            {/* Center VS divider badge (md+ only) */}
            <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 z-10 hidden -translate-x-1/2 -translate-y-1/2 md:block">
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-slate-950 font-mono text-[11px] font-bold tracking-widest text-slate-300 shadow-xl shadow-black/40">
                VS
              </span>
            </div>
            {/* The Old Way — formal enterprise card */}
            <R className="h-full">
              <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-red-500/20 bg-[var(--bg-card)] transition-all duration-300 hover:border-red-500/35 motion-reduce:transition-none motion-reduce:hover:transform-none">
                {/* Top accent border */}
                <div aria-hidden="true" className="h-1 w-full bg-gradient-to-r from-red-500/80 via-red-400/40 to-transparent" />
                {/* Header band with status pill */}
                <div className="flex items-start justify-between gap-4 border-b border-white/[0.06] px-7 pb-5 pt-6">
                  <div>
                    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-red-400">
                      <X className="h-4 w-4" /> Méthode Traditionnelle (Tableur Excel)
                    </div>
                    <h3 className="mt-3 text-xl font-bold leading-snug text-white">
                      Fichiers cloisonnés, perte de traçabilité & livrables douloureux
                    </h3>
                  </div>
                  <span className="mt-0.5 inline-flex shrink-0 items-center gap-1.5 rounded-full border border-red-500/30 bg-red-500/10 px-2.5 py-1 font-mono text-[10px] font-bold tracking-wider text-red-300">
                    <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
                    À ÉVITER
                  </span>
                </div>
                {/* Numbered pain points with hairline dividers */}
                <ul className="flex-1 divide-y divide-red-500/10 px-7 text-sm text-slate-300">
                  <li className="flex items-start gap-3.5 py-4">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-red-500/25 bg-red-500/[0.08]">
                      <X className="h-3.5 w-3.5 text-red-400" />
                    </span>
                    <span className="leading-relaxed"><strong className="font-semibold text-slate-100">Désynchronisation permanente :</strong> versions multiples circulant par e-mail, écrasement accidentel des données d'atelier.</span>
                  </li>
                  <li className="flex items-start gap-3.5 py-4">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-red-500/25 bg-red-500/[0.08]">
                      <X className="h-3.5 w-3.5 text-red-400" />
                    </span>
                    <span className="leading-relaxed"><strong className="font-semibold text-slate-100">Scénarios déconnectés :</strong> modes opératoires rédigés à la main sans lien avec les référentiels réels d'attaques (ATT&CK).</span>
                  </li>
                  <li className="flex items-start gap-3.5 py-4">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-red-500/25 bg-red-500/[0.08]">
                      <X className="h-3.5 w-3.5 text-red-400" />
                    </span>
                    <span className="leading-relaxed"><strong className="font-semibold text-slate-100">Plans de traitement en doublon :</strong> mesures de sécurité éparpillées sans vision consolidée pour les arbitrages budgétaires.</span>
                  </li>
                  <li className="flex items-start gap-3.5 py-4">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-red-500/25 bg-red-500/[0.08]">
                      <X className="h-3.5 w-3.5 text-red-400" />
                    </span>
                    <span className="leading-relaxed"><strong className="font-semibold text-slate-100">Semaines perdues en mise en page :</strong> consolidation manuelle fastidieuse pour produire un rapport d'audit.</span>
                  </li>
                </ul>
                {/* Footer result bar */}
                <div className="mt-auto border-t border-red-500/15 bg-red-500/[0.07] px-7 py-4 font-mono text-xs text-red-300/90">
                  Résultat : Analyses de risques figées, obsolètes dès leur publication.
                </div>
              </div>
            </R>

            {/* The EBIOSENS Way — formal enterprise card, visually dominant */}
            <R delay={0.12} className="h-full">
              <div className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-emerald-400/30 bg-[var(--bg-card)] shadow-xl shadow-black/20 transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-300/40 motion-reduce:transition-none motion-reduce:hover:transform-none">
                {/* Top accent border */}
                <div aria-hidden="true" className="h-1 w-full bg-gradient-to-r from-emerald-400 via-emerald-300/60 to-transparent" />
                {/* Header band with status pill */}
                <div className="flex items-start justify-between gap-4 border-b border-white/[0.06] px-7 pb-5 pt-6">
                  <div>
                    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-300">
                      <CheckCircle2 className="h-4 w-4" /> La Solution EBIOSENS
                    </div>
                    <h3 className="mt-3 text-xl font-bold leading-snug text-white">
                      Workflow guidé, CTI intégrée & consolidation automatisée
                    </h3>
                  </div>
                  <span className="mt-0.5 inline-flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1 font-mono text-[10px] font-bold tracking-wider text-emerald-200">
                    <CheckCircle2 className="h-3 w-3" />
                    RECOMMANDÉ
                  </span>
                </div>
                {/* Icon-led items with hairline dividers */}
                <ul className="flex-1 divide-y divide-emerald-400/10 px-7 text-sm text-slate-200">
                  <li className="flex items-start gap-3.5 py-4">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
                    <span className="leading-relaxed"><strong className="font-semibold text-white">Workflow guidé pas à pas :</strong> les 5 ateliers EBIOS RM interconnectés avec validation continue et indicateurs de complétion.</span>
                  </li>
                  <li className="flex items-start gap-3.5 py-4">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
                    <span className="leading-relaxed"><strong className="font-semibold text-white">Threat-Informed Defense :</strong> modélisation des cyberattaques appuyée directement sur la base MITRE ATT&CK.</span>
                  </li>
                  <li className="flex items-start gap-3.5 py-4">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
                    <span className="leading-relaxed"><strong className="font-semibold text-white">Moteur Fusion PTR™ exclusif :</strong> mutualisation automatique des mesures pour optimiser vos budgets SSI.</span>
                  </li>
                  <li className="flex items-start gap-3.5 py-4">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
                    <span className="leading-relaxed"><strong className="font-semibold text-white">Assistance de l'intelligence artificielle :</strong> aide à la rédaction, suggestions d'événements redoutés, de scénarios et de mesures pour accélérer chaque atelier.</span>
                  </li>
                  <li className="flex items-start gap-3.5 py-4">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
                    <span className="leading-relaxed"><strong className="font-semibold text-white">Livrables conformes en 1 clic :</strong> génération instantanée de dossiers Word complets et dashboards de restitution COMEX.</span>
                  </li>
                </ul>
                {/* Footer result bar */}
                <div className="mt-auto border-t border-emerald-400/20 bg-emerald-400/[0.08] px-7 py-4 font-mono text-xs font-bold text-emerald-200">
                  Résultat : Un pilotage dynamique des risques et des audits réussis.
                </div>
              </div>
            </R>
          </div>
        </div>
      </section>

      {/* INTERACTIVE 5 WORKSHOPS STUDIO EXPLORER */}
      <section id="ateliers" className="relative z-10 py-20 lg:py-28 border-t border-[var(--bd)] bg-white/[0.01]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            number="02"
            badge="MÉTHODOLOGIE EBIOS RM"
            title="Les 5 Ateliers EBIOS RM, étape par étape."
            subtitle="Explorez comment EBIOSENS structure et guide chacun des 5 ateliers pour transformer la méthode EBIOS RM en un dossier d'analyse rigoureux et auditable."
          />

          <WorkshopsStudio />
        </div>
      </section>

      {/* CORE DIFFERENTIATORS SECTION */}
      <section id="differentiateurs" className="relative z-10 py-20 lg:py-28 border-t border-[var(--bd)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            number="03"
            badge="FONCTIONNALITÉS CLÉS"
            title="Conçu pour le terrain et la collaboration."
            subtitle="Des capacités pensées pour les besoins réels des risk managers."
          />

          <div className="grid md:grid-cols-2 gap-8 mt-12">
            {keyDifferentiators.map((diff, idx) => {
              const DiffIcon = diff.icon;
              return (
                <R key={diff.title} delay={idx * 0.08}>
                  <div className="h-full rounded-2xl border border-white/10 bg-[var(--bg-card)] p-8 hover:border-blue-500/40 hover:bg-[var(--bg-elevated)] transition-all group shadow-lg">
                    <div className="flex items-center justify-between mb-6">
                      {diff.logos ? (
                        <div className="h-12 flex items-center gap-2.5 rounded-xl bg-white border border-blue-500/20 px-3 group-hover:border-blue-500/50 transition-colors">
                          <img
                            src={diff.logos[0].src}
                            alt={diff.logos[0].alt}
                            className="h-5 w-auto object-contain"
                            loading="lazy"
                          />
                          <span aria-hidden="true" className="h-6 w-px bg-slate-300" />
                          <img
                            src={diff.logos[1].src}
                            alt={diff.logos[1].alt}
                            className="h-8 w-8 rounded object-contain"
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                          <DiffIcon className="w-6 h-6" />
                        </div>
                      )}
                      <span className="text-[11px] font-mono font-bold tracking-wider px-3 py-1 rounded-full bg-white/5 text-slate-300 border border-white/10">
                        {diff.badge}
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-200 transition-colors">
                      {diff.title}
                    </h3>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      {diff.desc}
                    </p>
                  </div>
                </R>
              );
            })}
          </div>
        </div>
      </section>

      {/* INTERACTIVE MATRIX & SIMULATION */}
      <section id="matrice" className="relative z-10 py-20 lg:py-28 border-t border-[var(--bd)] bg-white/[0.01]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            number="04"
            badge="DÉCISION & COMEX"
            title="Visualisez l’impact concret de vos mesures sur le risque résiduel."
            subtitle="Basculez entre la vision des Risques Bruts et celle des Risques Résiduels pour démontrer à votre direction générale l’efficacité de votre Plan de Traitement des Risques."
          />

          <InteractiveMatrix />
        </div>
      </section>

      {/* OFFICIAL DELIVERABLES & REPORTING */}
      <section id="livrables" className="relative z-10 py-20 lg:py-28 border-t border-[var(--bd)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            number="05"
            badge="LIVRABLES RÉGLEMENTAIRES"
            title="Exporter vos livrables avec simplicité selon le format souhaité."
            subtitle="Exportez vos livrables aux standards attendus par les risk managers et les comités de direction."
          />

          <div className="grid lg:grid-cols-3 gap-8 mt-12">
            {/* Word Export Card */}
            <R delay={0}>
              <div className="h-full rounded-2xl border border-white/10 bg-[var(--bg-card)] p-8 flex flex-col justify-between hover:border-blue-500/30 transition-all">
                <div>
                  <div className="w-12 h-12 rounded-xl bg-blue-600/15 border border-blue-500/30 flex items-center justify-center text-blue-400 mb-6">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div className="text-xs font-mono font-bold text-blue-400 mb-1">DOCUMENT OFFICIEL</div>
                  <h3 className="text-xl font-bold text-white mb-3">Rapport d’Analyse Word (.docx)</h3>
                  <p className="text-sm text-slate-300 leading-relaxed mb-6">
                    Générez automatiquement le dossier complet respectant fidèlement le canevas type préconisé par le référentiel : fiches de cadrage, cartographie écosystème, scénarios d'attaque et annexes complètes.
                  </p>
                </div>
                <div className="pt-4 border-t border-white/10 flex items-center justify-between text-xs text-slate-400 font-mono">
                  <span>Modèle personnalisé</span>
                  <span className="text-emerald-400 font-bold">1-Clic</span>
                </div>
              </div>
            </R>

            {/* Executive HTML Dashboard */}
            <R delay={0.08}>
              <div className="h-full rounded-2xl border border-white/10 bg-[var(--bg-card)] p-8 flex flex-col justify-between hover:border-blue-500/30 transition-all">
                <div>
                  <div className="w-12 h-12 rounded-xl bg-indigo-600/15 border border-indigo-500/30 flex items-center justify-center text-indigo-400 mb-6">
                    <BarChart3 className="w-6 h-6" />
                  </div>
                  <div className="text-xs font-mono font-bold text-indigo-400 mb-1">RESTITUTION COMEX</div>
                  <h3 className="text-xl font-bold text-white mb-3">Dashboard Exécutif HTML</h3>
                  <p className="text-sm text-slate-300 leading-relaxed mb-6">
                    Un portail web interactif et visuel pour présenter l’analyse aux décideurs non spécialistes : matrices dynamiques, synthèse des investissements cyber et tableau de bord des risques prioritaires.
                  </p>
                </div>
                <div className="pt-4 border-t border-white/10 flex items-center justify-between text-xs text-slate-400 font-mono">
                  <span>Interactif & Graphique</span>
                  <span className="text-emerald-400 font-bold">Autonome</span>
                </div>
              </div>
            </R>

            {/* CSV Data & SIEM/GRC Export */}
            <R delay={0.16}>
              <div className="h-full rounded-2xl border border-white/10 bg-[var(--bg-card)] p-8 flex flex-col justify-between hover:border-blue-500/30 transition-all">
                <div>
                  <div className="w-12 h-12 rounded-xl bg-sky-600/15 border border-sky-500/30 flex items-center justify-center text-sky-400 mb-6">
                    <FileSpreadsheet className="w-6 h-6" />
                  </div>
                  <div className="text-xs font-mono font-bold text-sky-400 mb-1">INTÉGRATION GRC & SIEM</div>
                  <h3 className="text-xl font-bold text-white mb-3">Données Structurées CSV</h3>
                  <p className="text-sm text-slate-300 leading-relaxed mb-6">
                    Exportez l'intégralité des tableaux de données (biens supports, événements redoutés, scénarios opérationnels, mesures PTR) au format CSV standard pour vos outils de GRC tiers.
                  </p>
                </div>
                <div className="pt-4 border-t border-white/10 flex items-center justify-between text-xs text-slate-400 font-mono">
                  <span>Norme RFC 4180</span>
                  <span className="text-emerald-400 font-bold">Interopérable</span>
                </div>
              </div>
            </R>
          </div>
        </div>
      </section>

      {/* ENTERPRISE SECURITY, COMPLIANCE & SOVEREIGNTY */}
      <section id="securite" className="relative z-10 py-20 lg:py-28 border-t border-[var(--bd)] bg-white/[0.01]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            number="06"
            badge="SÉCURITÉ & SOUVERAINETÉ"
            title="Vos analyses les plus sensibles, centralisées et mieux protégées."
            subtitle="Une architecture sécurisée et adaptable, conçue pour répondre aux besoins de toutes les organisations, quelle que soit leur taille ou leur niveau d'exigence."
          />

          <div className="rounded-2xl border border-white/10 bg-[var(--bg-card)] p-8 lg:p-12 shadow-2xl">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div>
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 mb-4">
                  <Server className="w-5 h-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-2">Déploiement flexible</h4>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Déployez EBIOSENS sur vos propres serveurs ou utilisez notre version SaaS. Une approche flexible, adaptée à vos contraintes d’hébergement, de sécurité et de confidentialité.
                </p>
              </div>

              <div>
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 mb-4">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-2">Souveraineté France / UE</h4>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Solution éditée et développée en France par ACG Cybersecurity. Données exclusivement régies par le droit européen et le RGPD.
                </p>
              </div>

              <div>
                <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 mb-4">
                  <Lock className="w-5 h-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-2">Chiffrement & RBAC Granulaire</h4>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Chiffrement de bout en bout et gestion stricte des privilèges : Superadmin, Admin d'organisation, Analyste Risques et profil Auditeur (lecture seule).
                </p>
              </div>

              <div>
                <div className="w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center text-sky-400 mb-4">
                  <History className="w-5 h-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-2">Traçabilité & Journal d’Audit</h4>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Chaque modification fait l'objet d'un enregistrement horodaté inaltérable. Historique de versions avec snapshots restaurables en cas d'erreur.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FREQUENTLY ASKED QUESTIONS (FAQ) */}
      <section className="relative z-10 py-20 lg:py-28 border-t border-[var(--bd)]">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            number="07"
            badge="QUESTIONS FRÉQUENTES"
            title="Tout ce que vous devez savoir sur EBIOSENS."
            subtitle="Des réponses précises à vos questions méthodologiques, techniques et contractuelles."
            centered
          />

          <div className="space-y-4 mt-12">
            {enterpriseFAQ.map((faqItem, idx) => {
              const isOpen = openFaqIndex === idx;
              return (
                <div
                  key={faqItem.q}
                  className={`rounded-xl border transition-all ${isOpen ? 'bg-[var(--bg-card)] border-blue-500/30 shadow-lg' : 'bg-white/[0.02] border-white/10 hover:border-white/20'}`}
                >
                  <button
                    onClick={() => setOpenFaqIndex(isOpen ? -1 : idx)}
                    className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
                  >
                    <span className="font-bold text-sm sm:text-base text-white">
                      {faqItem.q}
                    </span>
                    <span className={`w-7 h-7 rounded-full border flex items-center justify-center shrink-0 transition-transform ${isOpen ? 'border-blue-400 text-blue-400 rotate-180' : 'border-white/20 text-slate-400'}`}>
                      <ChevronDown className="w-4 h-4" />
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 pt-1 text-sm text-slate-300 leading-relaxed border-t border-white/5">
                          {faqItem.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL HIGH-IMPACT CTA SECTION */}
      <section className="relative z-10 py-20 lg:py-28 border-t border-[var(--bd)] overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
          <div className="rounded-3xl border border-blue-500/30 bg-gradient-to-br from-blue-950/60 via-slate-900/80 to-indigo-950/60 p-8 sm:p-14 lg:p-16 text-center relative overflow-hidden shadow-2xl">
            {/* Background Glow */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/15 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-500/15 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 max-w-3xl mx-auto">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-bold tracking-wider mb-6 border border-blue-500/30">
                PASSEZ AU NIVEAU SUPÉRIEUR
              </span>

              <h2 className="text-3xl sm:text-5xl font-black text-white tracking-tight leading-tight">
                Prêt à mener vos analyses EBIOS RM avec sérénité ?
              </h2>

              <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed">
                Rejoignez les équipes cybersécurité qui ont choisi EBIOSENS pour piloter leurs risques, préparer leurs certifications et éclairer leurs comités de direction.
              </p>

              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-3 bg-white text-slate-950 hover:bg-slate-100 font-extrabold text-sm sm:text-base px-8 py-4 rounded-xl shadow-xl transition-all hover:scale-[1.02]"
                >
                  <span>OUVRIR LA PLATEFORME</span>
                  <ArrowRight className="w-4 h-4 text-slate-900" />
                </Link>

                <a
                  href="#ateliers"
                  onClick={(e) => handleNavClick(e, 'ateliers')}
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/20 text-white font-bold text-sm sm:text-base px-7 py-4 rounded-xl backdrop-blur transition-colors"
                >
                  <span>CONSULTER LA MÉTHODE</span>
                </a>
              </div>

              <div className="mt-8 text-xs font-mono text-slate-400">
                CONFORME EBIOS RM v1.5 · ÉDITÉ PAR ACG CYBERSECURITY · PARIS, FRANCE
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROFESSIONAL FOOTER */}
      <footer className="relative z-10 border-t border-[var(--bd)] bg-slate-950/80">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            {/* Col 1: Brand Info */}
            <div className="md:col-span-4 space-y-4">
              <EbiosensLogo size={30} variant="dark" withWordmark />
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed max-w-sm">
                EBIOSENS est la plateforme de référence pour la conduite, la collaboration et la restitution d'analyses de risques selon la méthode EBIOS RM v1.5.
              </p>
              {/* Homepage footer logos — ANSSI/EBIOS RM logos hidden, ACG kept */}
              <div className="pt-2 flex items-center gap-3">
                <img src="/acg-logo.png" alt="ACG Cybersecurity" className="h-8 w-auto rounded bg-white p-1 border border-white/10" />
              </div>
            </div>

            {/* Col 2: Navigation Links */}
            <div className="md:col-span-2 space-y-3">
              <div className="text-xs font-bold uppercase tracking-wider text-blue-400">Navigation Plateforme</div>
              <ul className="space-y-2 text-xs sm:text-sm text-slate-300 font-medium">
                <li><a href="#ateliers" onClick={(e) => handleNavClick(e, 'ateliers')} className="hover:text-white transition-colors">Les 5 Ateliers EBIOS RM</a></li>
                <li><a href="#differentiateurs" onClick={(e) => handleNavClick(e, 'differentiateurs')} className="hover:text-white transition-colors">CTI MITRE ATT&CK & Fusion PTR</a></li>
                <li><a href="#matrice" onClick={(e) => handleNavClick(e, 'matrice')} className="hover:text-white transition-colors">Simulateur de Matrice 4×4</a></li>
                <li><a href="#livrables" onClick={(e) => handleNavClick(e, 'livrables')} className="hover:text-white transition-colors">Générateur de Rapports EBIOS RM</a></li>
                <li><a href="#securite" onClick={(e) => handleNavClick(e, 'securite')} className="hover:text-white transition-colors">Souveraineté & Sécurité On-Premise</a></li>
              </ul>
            </div>

            {/* Col 3: Compliance & Legal Notes */}
            <div className="md:col-span-3 space-y-3">
              <div className="text-xs font-bold uppercase tracking-wider text-blue-400">Mentions & Conformité</div>
              <p className="text-xs text-slate-400 leading-relaxed">
                EBIOS RM est une méthode déposée et publiée par l’ANSSI (Agence nationale de la sécurité des systèmes d'information). EBIOSENS est une solution logicielle indépendante éditée par ACG Cybersecurity.
              </p>
              <div className="pt-2 text-xs text-slate-400">
                <a href="https://acgcybersecurity.fr" target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 font-bold inline-flex items-center gap-1">
                  acgcybersecurity.fr <ArrowUpRight className="w-3 h-3" />
                </a>
              </div>
            </div>

            {/* Col 4: Contact */}
            <div className="md:col-span-3 space-y-3">
              <div className="text-xs font-bold uppercase tracking-wider text-blue-400">Contactez-nous</div>
              <ul className="space-y-2 text-xs sm:text-sm text-slate-300 font-medium">
                <li className="text-slate-400 leading-relaxed">Campus Cyber, Tour Eria 5 Rue Bellini, 92800 Puteaux</li>
                <li>
                  <a href="tel:+33189623430" className="hover:text-white transition-colors">+33 1 89 62 34 30</a>
                </li>
                <li className="text-slate-400">Lundi – Vendredi : 9h00 – 18h30</li>
                <li>
                  <a href="mailto:contact@acgcybersecurity.fr" className="text-blue-400 hover:text-blue-300 font-bold break-all transition-colors">contact@acgcybersecurity.fr</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
            <div>
              © {new Date().getFullYear()} ACG CYBERSECURITY — TOUS DROITS RÉSERVÉS · REF. EBS-2025-014
            </div>
            <div className="flex items-center gap-4 text-slate-400 font-mono text-[11px]">
              <span>EBIOS RM v1.5</span>
              <span>·</span>
              <span>MITRE ATT&CK v14</span>
              <span>·</span>
              <span>FRANCE / UE</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
